import React from "react";
import { Info } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EUROPEAN_COUNTRIES } from "../../../../data/countries";
import "./LenderManualLoanCasePersonalInfo.css";

const EMPTY_PRIMARY_APPLICANT = {
  id: null,
  first_name: "",
  last_name: "",
  date_of_birth: "",
  country_of_residence: "",
  residency_status: "",
  official_address: "",
  email: "",
  phone: "",
  marital_status: "",
  dependents: "",
  preferred_contact_method: "EMAIL",
};

const EMPTY_CO_APPLICANT = {
  id: null,
  first_name: "",
  last_name: "",
  date_of_birth: "",
  email: "",
};

const COUNTRY_OPTIONS = EUROPEAN_COUNTRIES.map((country) => ({
  value: country.code,
  label: country.label,
}));

const PHONE_CODE_OPTIONS = EUROPEAN_COUNTRIES.map((country) => ({
  value: country.phoneCode,
  label: `${country.label} (${country.phoneCode})`,
}));

const RESIDENCY_STATUS_OPTIONS = [
  {
    value: "CITIZEN",
    label: "Citizen of country of residence",
  },
  {
    value: "PERMANENT_RESIDENT",
    label: "Permanent resident",
  },
  {
    value: "LONG_TERM_EU_RESIDENT",
    label: "Long-term resident (EU)",
  },
  {
    value: "EU_BLUE_CARD",
    label: "EU Blue Card",
  },
  {
    value: "FAMILY_MEMBER_EU_EEA",
    label: "Family member of EU/EEA citizen",
  },
  {
    value: "TEMPORARY_RESIDENCE_PERMIT",
    label: "Temporary residence permit",
  },
  {
    value: "OTHER",
    label: "Other",
  },
];

const MARITAL_STATUS_OPTIONS = [
  {
    value: "SINGLE",
    label: "Single",
  },
  {
    value: "MARRIED",
    label: "Married",
  },
  {
    value: "LEGAL_PARTNERSHIP",
    label: "Legal partnership",
  },
  {
    value: "FACTUAL_PARTNERSHIP",
    label: "Factual partnership",
  },
  {
    value: "OTHER",
    label: "Other",
  },
];

const CONTACT_METHOD_OPTIONS = [
  {
    value: "EMAIL",
    label: "Email",
  },
  {
    value: "PHONE",
    label: "Phone call",
  },
];

function hasValue(value) {
  return String(value ?? "").trim().length > 0;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function isValidPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 6;
}

function toDateInputValue(value) {
  if (!value) return "";

  const text = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  const EuropeanDate = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (EuropeanDate) {
    const [, day, month, year] = EuropeanDate;
    return `${year}-${month}-${day}`;
  }

  return "";
}

function isValidDate(value) {
  if (!value) return false;

  const parsed = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) {
    return false;
  }

  return parsed <= new Date();
}

function getPhoneCodeByCountry(countryCode) {
  const matchingCountry = EUROPEAN_COUNTRIES.find(
    (country) =>
      country.code ===
      String(countryCode || "")
        .trim()
        .toUpperCase(),
  );

  return matchingCountry?.phoneCode || "";
}

function splitPhoneValue(value = "") {
  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return {
      phoneCode: "+32",
      phoneNumber: "",
    };
  }

  const sortedCountries = [...EUROPEAN_COUNTRIES].sort(
    (first, second) => second.phoneCode.length - first.phoneCode.length,
  );

  const matchingCountry = sortedCountries.find((country) =>
    rawValue.startsWith(country.phoneCode),
  );

  if (matchingCountry) {
    return {
      phoneCode: matchingCountry.phoneCode,
      phoneNumber: rawValue.slice(matchingCountry.phoneCode.length).trim(),
    };
  }

  const prefixMatch = rawValue.match(/^(\+\d{1,4})(.*)$/);

  if (prefixMatch) {
    return {
      phoneCode: prefixMatch[1],
      phoneNumber: prefixMatch[2].trim(),
    };
  }

  return {
    phoneCode: "+32",
    phoneNumber: rawValue,
  };
}

function buildPhoneValue(phoneCode, phoneNumber) {
  const cleanCode = String(phoneCode || "").trim();

  const cleanNumber = String(phoneNumber || "")
    .replace(/[^\d\s()-]/g, "")
    .trim();

  if (!cleanNumber) {
    return "";
  }

  return `${cleanCode}${cleanNumber}`;
}

function normalizePrimaryApplicant(source = {}) {
  return {
    ...EMPTY_PRIMARY_APPLICANT,
    ...source,

    date_of_birth: toDateInputValue(source.date_of_birth),

    official_address: source.official_address || source.address || "",

    dependents:
      source.dependents === null || source.dependents === undefined
        ? ""
        : String(source.dependents),

    preferred_contact_method: source.preferred_contact_method || "EMAIL",
  };
}

function normalizeCoApplicant(source = {}) {
  return {
    ...EMPTY_CO_APPLICANT,
    ...source,
    date_of_birth: toDateInputValue(source.date_of_birth),
  };
}

function FormField({
  label,
  name,
  value,
  onChange,
  required = false,
  full = false,
  type = "text",
  options = null,
  placeholder = "",
  min,
  max,
  inputMode,
  error = "",
  tooltip = "",
  tooltipLabel = "More information",
}) {
  const isSelect = Array.isArray(options) && options.length > 0;

  const [hasFocusWithin, setHasFocusWithin] = React.useState(false);

  return (
    <div
      className={`lender-personal-step__field ${
        full ? "lender-personal-step__field--full" : ""
      } ${error ? "lender-personal-step__field--invalid" : ""}`}
      onFocusCapture={() => {
        setHasFocusWithin(true);
      }}
      onBlurCapture={(event) => {
        const nextFocusedElement = event.relatedTarget;

        if (!event.currentTarget.contains(nextFocusedElement)) {
          setHasFocusWithin(false);
        }
      }}
    >
      <div className="lender-personal-step__label-row">
        <div className="lender-personal-step__label-group">
          <label
            className="lender-personal-step__label"
            htmlFor={`manual-personal-${name}`}
          >
            {label}

            {required ? (
              <span
                className="lender-personal-step__required"
                aria-hidden="true"
              >
                *
              </span>
            ) : null}
          </label>

          {tooltip ? (
            <FieldTooltip
              text={tooltip}
              accessibleLabel={tooltipLabel}
              forceOpen={hasFocusWithin}
            />
          ) : null}
        </div>
      </div>

      {isSelect ? (
        <select
          id={`manual-personal-${name}`}
          className="lender-personal-step__input lender-personal-step__select"
          value={value ?? ""}
          onChange={(event) => onChange(name, event.target.value)}
          required={required}
          aria-invalid={Boolean(error)}
        >
          <option value="">{placeholder || "Select"}</option>

          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={`manual-personal-${name}`}
          className="lender-personal-step__input"
          type={type}
          value={value ?? ""}
          onChange={(event) => onChange(name, event.target.value)}
          placeholder={placeholder}
          required={required}
          min={min}
          max={max}
          inputMode={inputMode}
          aria-invalid={Boolean(error)}
        />
      )}
      {error ? <span className="lender-personal-step__field-error" role="alert">{error}</span> : null}
    </div>
  );
}

function PhoneField({ t, value, onChange, required = false, error = "", fallbackCode = "+32" }) {
  const { phoneCode, phoneNumber } = splitPhoneValue(value);
  const selectedPhoneCode = value ? phoneCode : fallbackCode;

  return (
    <div className={`lender-personal-step__field ${error ? "lender-personal-step__field--invalid" : ""}`}>
      <div className="lender-personal-step__label-row">
        <label className="lender-personal-step__label">
          {t("fields.phoneNumber", "Phone number")}

          {required ? (
            <span className="lender-personal-step__required" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      </div>

      <div className="lender-personal-step__phone-row">
        <select
          className="lender-personal-step__input lender-personal-step__select lender-personal-step__phone-code"
          value={selectedPhoneCode}
          onChange={(event) =>
            onChange(buildPhoneValue(event.target.value, phoneNumber))
          }
          aria-label={t("fields.phoneCountryCode", "Phone country code")}
        >
          {PHONE_CODE_OPTIONS.map((option) => (
            <option
              key={`${option.label}-${option.value}`}
              value={option.value}
            >
              {option.value}
            </option>
          ))}
        </select>

        <input
          className="lender-personal-step__input lender-personal-step__phone-number"
          type="tel"
          value={phoneNumber}
          onChange={(event) =>
            onChange(buildPhoneValue(selectedPhoneCode, event.target.value))
          }
          placeholder={t("placeholders.phoneNumber", "Phone number")}
          inputMode="tel"
          required={required}
          aria-invalid={Boolean(error)}
        />
      </div>
      {error ? <span className="lender-personal-step__field-error" role="alert">{error}</span> : null}
    </div>
  );
}

function FieldTooltip({
  text,
  accessibleLabel = "More information",
  forceOpen = false,
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isClickedOpen, setIsClickedOpen] = React.useState(false);
  const tooltipId = React.useId();

  if (!text) {
    return null;
  }

  const isOpen = forceOpen || isHovered || isClickedOpen;

  return (
    <span
      className={`lender-personal-step__tooltip ${
        isOpen ? "lender-personal-step__tooltip--open" : ""
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        className="lender-personal-step__tooltip-trigger"
        aria-label={accessibleLabel}
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-expanded={isOpen}
        onFocus={() => setIsClickedOpen(true)}
        onBlur={() => setIsClickedOpen(false)}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          setIsClickedOpen(true);
        }}
      >
        <Info size={14} strokeWidth={2.2} />
      </button>

      <span
        id={tooltipId}
        className="lender-personal-step__tooltip-content"
        role="tooltip"
        aria-hidden={!isOpen}
      >
        {text}
      </span>
    </span>
  );
}
export default function LenderManualPersonalInfo() {
  const { t } = useTranslation("lender-personal-info");

  const {
    loanCaseData,
    loanCaseId,
    currentStepKey,
    stepValues,
    handleStepValidityChange,
    handleStepValuesChange,
    validationRequest,
  } = useOutletContext();

  const [primaryApplicant, setPrimaryApplicant] = React.useState(
    EMPTY_PRIMARY_APPLICANT,
  );

  const [coApplicant, setCoApplicant] = React.useState(null);

  const initializedCaseRef = React.useRef(null);

  const dossierCountryCode =
    loanCaseData?.country_code || loanCaseData?.dossier?.country_code || "";

  const requiresResidencyStatus = dossierCountryCode === "ES";

  const today = React.useMemo(() => new Date().toISOString().slice(0, 10), []);

  React.useEffect(() => {
    if (!loanCaseData || !loanCaseId) {
      return;
    }

    if (initializedCaseRef.current === String(loanCaseId)) {
      return;
    }

    const storedStep = stepValues?.[currentStepKey] || {};

    const storedPersonalInfo = storedStep.personal_info || storedStep;

    const primarySource =
      storedPersonalInfo.primary_applicant ||
      loanCaseData.primary_applicant ||
      EMPTY_PRIMARY_APPLICANT;

    const coSource =
      storedPersonalInfo.co_applicant ?? loanCaseData.co_applicant ?? null;

    setPrimaryApplicant(normalizePrimaryApplicant(primarySource));

    setCoApplicant(coSource ? normalizeCoApplicant(coSource) : null);

    initializedCaseRef.current = String(loanCaseId);
  }, [currentStepKey, loanCaseData, loanCaseId, stepValues]);

  const handlePrimaryChange = React.useCallback(
    (name, value) => {
      setPrimaryApplicant((previous) => {
        const next = {
          ...previous,
          [name]: value,
        };

        if (name === "dependents") {
          next.dependents = String(value || "").replace(/\D/g, "");
        }

        if (name === "country_of_residence") {
          const newPhoneCode = getPhoneCodeByCountry(value);

          const currentPhone = splitPhoneValue(previous.phone);

          if (newPhoneCode && !currentPhone.phoneNumber) {
            next.phone = "";
          }

          if (!requiresResidencyStatus && value !== "ES") {
            next.residency_status = "";
          }
        }

        return next;
      });
    },
    [requiresResidencyStatus],
  );

  const handleCoApplicantChange = React.useCallback((name, value) => {
    setCoApplicant((previous) => ({
      ...(previous || EMPTY_CO_APPLICANT),
      [name]: value,
    }));
  }, []);

  // Match the actual fields rendered by this page and expose each failure.
  const validationErrors = React.useMemo(() => {
    const primary = {};
    const co = {};
    const required = (key, value) => {
      if (!hasValue(value)) primary[key] = t("validation.required", "This field is required.");
    };
    required("first_name", primaryApplicant.first_name);
    required("last_name", primaryApplicant.last_name);
    if (!isValidDate(primaryApplicant.date_of_birth)) {
      primary.date_of_birth = t("validation.validDate", "Enter a valid date of birth in the past.");
    }
    required("country_of_residence", primaryApplicant.country_of_residence);
    if (requiresResidencyStatus) required("residency_status", primaryApplicant.residency_status);
    required("official_address", primaryApplicant.official_address);
    if (!isValidEmail(primaryApplicant.email)) {
      primary.email = t("validation.validEmail", "Enter a valid email address.");
    }
    if (!isValidPhone(primaryApplicant.phone)) {
      primary.phone = t("validation.validPhone", "Enter a valid phone number.");
    }
    required("marital_status", primaryApplicant.marital_status);
    if (!/^\d+$/.test(String(primaryApplicant.dependents ?? "").trim())) {
      primary.dependents = t("validation.dependents", "Enter the number of dependents (use 0 if none).");
    }
    required("preferred_contact_method", primaryApplicant.preferred_contact_method);

    if (coApplicant) {
      if (!hasValue(coApplicant.first_name)) co.first_name = t("validation.required", "This field is required.");
      if (!hasValue(coApplicant.last_name)) co.last_name = t("validation.required", "This field is required.");
      if (hasValue(coApplicant.email) && !isValidEmail(coApplicant.email)) {
        co.email = t("validation.validEmail", "Enter a valid email address.");
      }
      if (hasValue(coApplicant.date_of_birth) && !isValidDate(coApplicant.date_of_birth)) {
        co.date_of_birth = t("validation.validDate", "Enter a valid date of birth in the past.");
      }
    }
    return { primary, co };
  }, [primaryApplicant, coApplicant, requiresResidencyStatus, t]);

  const stepIsValid = Object.keys(validationErrors.primary).length === 0 &&
    Object.keys(validationErrors.co).length === 0;
  const [showValidationErrors, setShowValidationErrors] = React.useState(false);
  const validationRequestRef = React.useRef(validationRequest);
  React.useEffect(() => {
    if (validationRequest === validationRequestRef.current) return;
    validationRequestRef.current = validationRequest;
    setShowValidationErrors(true);
    window.requestAnimationFrame(() => {
      const firstInvalid = document.querySelector(".lender-personal-step__field--invalid input, .lender-personal-step__field--invalid select");
      firstInvalid?.focus({ preventScroll: true });
      firstInvalid?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [validationRequest]);

  const primaryLabels = {
    first_name: t("fields.firstName", "First name"),
    last_name: t("fields.lastName", "Last name"),
    date_of_birth: t("fields.dateOfBirth", "Date of birth"),
    country_of_residence: t("fields.countryOfResidence", "Country of residence"),
    residency_status: t("fields.residencyStatus", "Residency status"),
    official_address: t("fields.address", "Address from ID"),
    email: t("fields.email", "Email"),
    phone: t("fields.phoneNumber", "Phone number"),
    marital_status: t("fields.maritalStatus", "Marital status"),
    dependents: t("fields.dependents", "Dependents"),
    preferred_contact_method: t("fields.preferredContactMethod", "Preferred contact method"),
  };
  const missingFields = [
    ...Object.keys(validationErrors.primary).map(key => primaryLabels[key] || key),
    ...Object.keys(validationErrors.co).map(key => `Co-borrower: ${primaryLabels[key] || key}`),
  ];
  React.useEffect(() => {
    if (initializedCaseRef.current !== String(loanCaseId)) {
      return;
    }

    handleStepValidityChange(currentStepKey, stepIsValid, missingFields);

    handleStepValuesChange(currentStepKey, {
      primary_applicant: {
        ...primaryApplicant,

        dependents:
          primaryApplicant.dependents === ""
            ? null
            : Number(primaryApplicant.dependents),
      },

      co_applicant: coApplicant
        ? {
            ...coApplicant,
          }
        : null,

      communication: {
        preferred_contact_method: primaryApplicant.preferred_contact_method,
      },
    });
  }, [
    coApplicant,
    currentStepKey,
    handleStepValidityChange,
    handleStepValuesChange,
    loanCaseId,
    primaryApplicant,
    stepIsValid,
  ]);

  return (
    <div className="lender-personal-step">
      <div
        className={`lender-personal-step__grid ${
          coApplicant
            ? "lender-personal-step__grid--with-co"
            : "lender-personal-step__grid--no-co"
        }`}
      >
        <section className="lender-personal-step__card lender-personal-step__card--accent lender-personal-step__card--main">
          <div className="lender-personal-step__card-header">
            <div>
              <h3>{t("sections.primaryApplicant", "Primary applicant")}</h3>

              <p className="lender-personal-step__helper">
                {t(
                  "sections.primaryApplicantDescription",
                  "Enter the personal and contact details provided by the homebuyer.",
                )}
              </p>
            </div>
          </div>

          <div className="lender-personal-step__fields">
            <FormField
              label={t("fields.firstName", "First name")}
              name="first_name"
              error={showValidationErrors ? validationErrors.primary.first_name : ""}
              value={primaryApplicant.first_name}
              onChange={handlePrimaryChange}
              required
            />

            <FormField
              label={t("fields.lastName", "Last name")}
              name="last_name"
              error={showValidationErrors ? validationErrors.primary.last_name : ""}
              value={primaryApplicant.last_name}
              onChange={handlePrimaryChange}
              required
            />

            <FormField
              label={t("fields.dateOfBirth", "Date of birth")}
              name="date_of_birth"
              error={showValidationErrors ? validationErrors.primary.date_of_birth : ""}
              type="date"
              value={primaryApplicant.date_of_birth}
              onChange={handlePrimaryChange}
              max={today}
              required
            />

            <FormField
              label={t("fields.countryOfResidence", "Country of residence")}
              name="country_of_residence"
              error={showValidationErrors ? validationErrors.primary.country_of_residence : ""}
              value={primaryApplicant.country_of_residence}
              onChange={handlePrimaryChange}
              options={COUNTRY_OPTIONS}
              placeholder={t("placeholders.selectCountry", "Select country")}
              required
            />

            {requiresResidencyStatus ? (
              <FormField
                label={t("fields.residencyStatus", "Residency status")}
                name="residency_status"
                value={primaryApplicant.residency_status}
                onChange={handlePrimaryChange}
                options={RESIDENCY_STATUS_OPTIONS}
                error={showValidationErrors ? validationErrors.primary.residency_status : ""}
                placeholder={t("placeholders.selectResidencyStatus", "Select residency status")}
                required
              />
            ) : null}

            <FormField
              label={t("fields.address", "Address from ID")}
              name="official_address"
              error={showValidationErrors ? validationErrors.primary.official_address : ""}
              value={primaryApplicant.official_address}
              onChange={handlePrimaryChange}
              placeholder={t(
                "placeholders.officialAddress",
                "Enter the residential address shown on the ID",
              )}
              tooltip={t(
                "tooltips.officialAddress",
                "Enter the official residential address shown on the applicant's identification document.",
              )}
              tooltipLabel={t(
                "tooltips.moreInformation",
                "More information about this field",
              )}
              full
              required
            />

            <FormField
              label={t("fields.email", "Email")}
              name="email"
              error={showValidationErrors ? validationErrors.primary.email : ""}
              type="email"
              value={primaryApplicant.email}
              onChange={handlePrimaryChange}
              required
            />

            <PhoneField
              error={showValidationErrors ? validationErrors.primary.phone : ""}
              fallbackCode={getPhoneCodeByCountry(primaryApplicant.country_of_residence || dossierCountryCode) || "+32"}
              t={t}
              value={primaryApplicant.phone}
              onChange={(value) => handlePrimaryChange("phone", value)}
              required
            />

            <FormField
              label={t("fields.maritalStatus", "Marital status")}
              name="marital_status"
              error={showValidationErrors ? validationErrors.primary.marital_status : ""}
              value={primaryApplicant.marital_status}
              onChange={handlePrimaryChange}
              options={MARITAL_STATUS_OPTIONS}
              placeholder={t("placeholders.selectStatus", "Select status")}
              required
            />

            <FormField
              label={t("fields.dependents", "Dependents")}
              name="dependents"
              error={showValidationErrors ? validationErrors.primary.dependents : ""}
              type="number"
              value={primaryApplicant.dependents}
              onChange={handlePrimaryChange}
              min="0"
              inputMode="numeric"
              tooltip={t(
                "tooltips.dependents",
                "Include children or other people who are financially dependent on the applicant.",
              )}
              tooltipLabel={t(
                "tooltips.moreInformation",
                "More information about dependents",
              )}
              required
            />
          </div>
        </section>

        <div className="lender-personal-step__side">
          <section className="lender-personal-step__card lender-personal-step__card--accent">
            <div className="lender-personal-step__card-header lender-personal-step__card-header--with-badge">
              <div>
                <h3>{t("sections.coBorrower", "Co-borrower")}</h3>

                <p className="lender-personal-step__helper">
                  {t(
                    "sections.coBorrowerDescription",
                    "Add a co-borrower only when another person participates in the loan.",
                  )}
                </p>
              </div>

              <label className="lender-personal-step__optional-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(coApplicant)}
                  onChange={(event) => {
                    if (event.target.checked) {
                      setCoApplicant(normalizeCoApplicant(EMPTY_CO_APPLICANT));
                    } else {
                      setCoApplicant(null);
                    }
                  }}
                />

                <span>{t("fields.addCoBorrower", "Add co-borrower")}</span>
              </label>
            </div>

            {coApplicant ? (
              <div className="lender-personal-step__fields">
                <FormField
                  label={t("fields.firstName", "First name")}
                  name="first_name"
                  error={showValidationErrors ? validationErrors.co.first_name : ""}
                  value={coApplicant.first_name}
                  onChange={handleCoApplicantChange}
                  required
                />

                <FormField
                  label={t("fields.lastName", "Last name")}
                  name="last_name"
                  error={showValidationErrors ? validationErrors.co.last_name : ""}
                  value={coApplicant.last_name}
                  onChange={handleCoApplicantChange}
                  required
                />

                <FormField
                  label={t("fields.dateOfBirth", "Date of birth")}
                  name="date_of_birth"
                  error={showValidationErrors ? validationErrors.co.date_of_birth : ""}
                  type="date"
                  value={coApplicant.date_of_birth}
                  onChange={handleCoApplicantChange}
                  max={today}
                />

                <FormField
                  label={t("fields.email", "Email")}
                  name="email"
                  error={showValidationErrors ? validationErrors.co.email : ""}
                  type="email"
                  value={coApplicant.email}
                  onChange={handleCoApplicantChange}
                  full
                />
              </div>
            ) : (
              <div className="lender-personal-step__empty-optional">
                {t("sections.noCoBorrower", "No co-borrower has been added.")}
              </div>
            )}
          </section>

          <section className="lender-personal-step__card lender-personal-step__card--accent lender-personal-step__card--communication">
            <div className="lender-personal-step__card-header">
              <div>
                <h3>
                  {t("sections.contactPreferences", "Contact preferences")}
                </h3>

                <p className="lender-personal-step__helper">
                  {t(
                    "sections.contactPreferencesDescription",
                    "Select how the homebuyer prefers to be contacted.",
                  )}
                </p>
              </div>
            </div>

            <div className="lender-personal-step__fields lender-personal-step__fields--single">
              <FormField
                label={t(
                  "fields.preferredContactMethod",
                  "Preferred contact method",
                )}
                name="preferred_contact_method"
                value={primaryApplicant.preferred_contact_method}
                onChange={handlePrimaryChange}
                options={CONTACT_METHOD_OPTIONS}
                placeholder={t(
                  "placeholders.selectContactMethod",
                  "Select contact method",
                )}
                full
                required
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
